import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, TableCell, TableRow, Tooltip } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TodayTwoToneIcon from "@mui/icons-material/TodayTwoTone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  switchBackgroundColorBasedOnStatus,
  switchColorOnScheduleStatus,
} from "../../helpers/switchColor";
import { TypographyNormal, TypographySmall } from "../customComponent";
import { CommandScheduleIcon, ContentScheduleIcon } from "../../icons";
import { blue, purple } from "@mui/material/colors";
import { parseExpression } from "cron-parser";
import { formattedDate } from "../../helpers/dateFormat";
import { colorStyling } from "../../helpers/color";
import { SET_SCHEDULE_DETAIL } from "../../store/actionTypes/scheduleActionType";
import { PriorityIcon } from "../../helpers/scheduleHelper";
import LaunchIcon from "@mui/icons-material/Launch";

const ScheduleItem = ({ schedule, dispatch, index, openContent }) => {
  const navigate = useNavigate();
  const { schedule: detail } = useSelector((state) => state.schedule);

  const [hoveredContent, setHoveredContent] = useState(false);
  const { t } = useTranslation("schedule");

  const navigateToScheduleDetail = () => {
    const newDetail = {
      ...schedule,
      ...(detail.id === schedule.id && {
        content: detail.content,
        screenGroups: detail.screenGroups,
        screens: detail.screens,
      }),
    };
    dispatch({ type: SET_SCHEDULE_DETAIL, payload: newDetail });
    navigate("/schedule/detail/" + schedule.id);
  };
  const handleOpenContentDetail = (event, id) => {
    event.stopPropagation();
    openContent(id);
  };

  return (
    <TableRow
      key={schedule.name + schedule.id}
      onClick={() => {
        navigateToScheduleDetail();
      }}
      sx={{
        cursor: "pointer",
        backgroundColor:
          (index + 1) % 2 !== 0
            ? colorStyling.white.hover
            : colorStyling.white.full,
        "&:hover": {
          backgroundColor: colorStyling.white.lightblue,
        },
      }}
    >
      <TableCell sx={{ pl: 3, pr: 0 }}>
        {schedule.type.includes("CONTENT") ? (
          <Tooltip
            arrow
            disableInteractive
            placement="top"
            title={
              <TypographySmall sx={{ color: "white" }}>
                {t("item.contentScheduleTip")}
              </TypographySmall>
            }
          >
            <Box width={48} height={48}>
              <ContentScheduleIcon />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            arrow
            disableInteractive
            placement="top"
            title={
              <TypographySmall sx={{ color: "white" }}>
                {t("item.commandScheduleTip")}
              </TypographySmall>
            }
          >
            <Box width={48} height={48}>
              <CommandScheduleIcon />
            </Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {schedule.isApproved ? (
            <Tooltip title={t("item.approvedTip")} placement="top">
              <CheckCircleOutlineIcon
                fontSize="small"
                sx={{
                  mr: 0.5,
                  color: switchBackgroundColorBasedOnStatus("APPROVED"),
                  borderRadius: "16px",
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={t("item.notApprovedTip")} placement="top">
              <ErrorOutlineIcon
                fontSize="small"
                sx={{
                  mr: 0.5,
                  color: switchBackgroundColorBasedOnStatus("NOT_APPROVED"),
                  borderRadius: "16px",
                }}
              />
            </Tooltip>
          )}

          <PriorityIcon level={schedule?.priority?.name} />

          <Tooltip
            title={schedule?.name?.length > 30 ? schedule?.name : ""}
            placement="top-start"
          >
            <TypographyNormal>
              {schedule?.name?.length > 30
                ? schedule?.name?.substring(0, 27).trim() + "..."
                : schedule?.name}
            </TypographyNormal>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TypographySmall
            sx={{
              color: switchColorOnScheduleStatus(
                schedule.isActive ? "ACTIVE" : "PENDING"
              ),
              backgroundColor: switchBackgroundColorBasedOnStatus(
                schedule.isActive ? "ACTIVE" : "PENDING"
              ),
              borderRadius: "15px",
              p: 1,
              fontWeight: 600,
              minWidth: "80px",
            }}
          >
            <FiberManualRecordIcon
              sx={{
                color: switchColorOnScheduleStatus(
                  schedule.isActive ? "ACTIVE" : "PENDING"
                ),
                fontSize: 8,
                mr: "5px",
              }}
            />
            {schedule.isActive
              ? t("item.activeLabel")
              : t("item.inactiveLabel")}
          </TypographySmall>
        </Box>
      </TableCell>
      <TableCell>
        {" "}
        {schedule.frequency === "OFF" || schedule.frequency === null ? (
          <TypographyNormal
            display="flex"
            alignItems="center"
            sx={{
              width: 100,
              fontWeight: 600,
              color: blue[600],
              textTransform: "uppercase",
            }}
          >
            {schedule.type.includes("CONTENT") ? (
              <>
                {t("frequency.LOOP")}
                <Tooltip
                  arrow
                  disableInteractive
                  placement="top"
                  title={t("item.explainLOOP")}
                >
                  <InfoOutlinedIcon
                    fontSize="1rem"
                    sx={{ marginLeft: "3px" }}
                  />
                </Tooltip>
              </>
            ) : (
              t("frequency.ONCE")
            )}
          </TypographyNormal>
        ) : (
          <Tooltip
            title={
              <Box sx={{ fontSize: 14, m: 0.2 }}>
                {new Date(schedule.startAt) > new Date()
                  ? t("item.startAtTip") + formattedDate(schedule.startAt)
                  : !schedule.endAt ||
                    new Date(schedule.endAt) >
                      new Date(
                        parseExpression(schedule.cron, { tz: "GMT" }).next()
                      )
                  ? t("item.nextAtTip") +
                    formattedDate(
                      parseExpression(schedule.cron, { tz: "GMT" }).next()
                    )
                  : t("item.scheduleCompleteTip")}
              </Box>
            }
            placement="top"
            arrow
            leaveDelay={200}
          >
            <Box display={"flex"} sx={{ width: 100 }}>
              <TodayTwoToneIcon
                fontSize="small"
                sx={{
                  color:
                    !schedule.endAt ||
                    new Date(schedule.endAt) >
                      new Date(
                        parseExpression(schedule.cron, { tz: "GMT" }).next()
                      )
                      ? blue[700]
                      : "gray",
                  mr: 0.2,
                }}
              />
              <TypographyNormal
                sx={{
                  fontWeight: 600,
                  color: blue[600],
                  textTransform: "uppercase",
                }}
              >
                {t(`frequency.${schedule.frequency}`, {
                  defaultValue: null,
                }) ?? schedule.frequency}
              </TypographyNormal>
            </Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        {formattedDate(schedule?.startAt) +
          " - " +
          (schedule.endAt
            ? formattedDate(schedule.endAt)
            : t("item.endTimeIndefinitely"))}
      </TableCell>
      <Tooltip
        title={
          schedule.command === "PLAY_CONTENT" ? t("contentDetailHint") : ""
        }
        placement="top-start"
      >
        <TableCell
          onClick={(event) => {
            if (schedule.command === "PLAY_CONTENT")
              handleOpenContentDetail(event, schedule.contentId);
          }}
          onMouseEnter={() =>
            schedule.command === "PLAY_CONTENT" ? setHoveredContent(true) : null
          }
          onMouseLeave={() =>
            schedule.command === "PLAY_CONTENT"
              ? setHoveredContent(false)
              : null
          }
          sx={{
            ...(hoveredContent &&
              schedule.command === "PLAY_CONTENT" && {
                color: colorStyling.primary,
                textDecoration: "underline",
                fontWeight: 600,
              }),
          }}
        >
          <TypographyNormal
            sx={{
              width: 100,
              textAlign: "left",
              fontWeight: 600,
              color: purple[600],
              textTransform: "capitalize",
            }}
          >
            {schedule.command.replace(/_/g, " ").toLowerCase()}
          </TypographyNormal>

          {schedule.command === "PLAY_CONTENT" && (
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Tooltip
                title={
                  schedule?.content?.name?.length > 30
                    ? schedule?.content?.name
                    : ""
                }
                placement="top-start"
              >
                {schedule?.content?.name?.length > 30
                  ? schedule?.content?.name?.substring(0, 27).trim() + "..."
                  : schedule?.content?.name}
              </Tooltip>
              <LaunchIcon
                fontSize="xs"
                sx={{
                  color: hoveredContent
                    ? colorStyling.primary
                    : colorStyling.gray.label,
                }}
              />
            </Box>
          )}
        </TableCell>
      </Tooltip>
    </TableRow>
  );
};

export default ScheduleItem;
